import { defineComponent } from "../lib/component.ts";

interface NewsletterRefs {
  email?: HTMLInputElement;

  emailError?: HTMLParagraphElement;

  consent?: HTMLInputElement;

  consentError?: HTMLParagraphElement;

  submit?: HTMLButtonElement;

  formError?: HTMLParagraphElement;
}

interface ModalPanelLike extends HTMLElement {
  close: () => void;
}

function isModalPanelLike(el: Element | null): el is ModalPanelLike {
  if (!(el instanceof HTMLElement)) return false;
  const close: unknown = Reflect.get(el, "close");
  return typeof close === "function";
}

class NewsletterSignup extends defineComponent<NewsletterRefs>()([
  "email",
  "emailError",
  "submit",
  "formError",
]) {
  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("submit", this.#handleSubmit, { signal: this.signal });
    this.refs.email.addEventListener("input", this.#handleEmailInput, {
      signal: this.signal,
    });
    this.refs.consent?.addEventListener("change", this.#handleConsentChange, {
      signal: this.signal,
    });
  }

  #handleEmailInput = (): void => {
    const { email, emailError } = this.refs;
    email.removeAttribute("aria-invalid");
    emailError.hidden = true;
  };

  #handleConsentChange = (): void => {
    const { consent, consentError } = this.refs;
    consent?.removeAttribute("aria-invalid");
    if (consentError) consentError.hidden = true;
  };

  #handleSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    const { email, emailError, consent, consentError, formError, submit } = this.refs;
    const { apiKey, listId, loadingText } = this.dataset;

    emailError.hidden = true;
    if (consentError) consentError.hidden = true;
    formError.hidden = true;
    email.removeAttribute("aria-invalid");
    consent?.removeAttribute("aria-invalid");

    if (!apiKey || !listId) {
      this.#showError(formError, this.dataset.errorNetwork ?? "");
      return;
    }

    if (!email.checkValidity()) {
      email.setAttribute("aria-invalid", "true");
      this.#showError(emailError, this.dataset.errorInvalidEmail ?? "");
      email.focus();
      return;
    }

    if (consent && !consent.checked) {
      consent.setAttribute("aria-invalid", "true");
      this.#showError(consentError ?? emailError, this.dataset.errorConsentRequired ?? "");
      consent.focus();
      return;
    }

    const originalText = submit.textContent;
    submit.disabled = true;
    submit.textContent = loadingText ?? "";

    try {
      const url = new URL("https://a.klaviyo.com/client/subscriptions/");
      url.searchParams.set("company_id", apiKey);

      const { source, locale, country } = this.dataset;
      const properties = this.#collectProperties();

      const profileAttributes: Record<string, unknown> = { email: email.value };
      if (locale) profileAttributes.locale = locale;
      if (country) profileAttributes.location = { country };
      profileAttributes.properties = properties;

      const subscriptionAttributes: Record<string, unknown> = {
        profile: {
          data: { type: "profile", attributes: profileAttributes },
        },
      };
      if (source) subscriptionAttributes.custom_source = source;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: subscriptionAttributes,
            relationships: {
              list: {
                data: { type: "list", id: listId },
              },
            },
          },
        }),
      });

      if (response.status === 202) {
        window.Shopify?.analytics?.publish?.("newsletter_signup", {
          method: "klaviyo",
          source: source ?? null,
          list_id: listId,
        });
        email.value = "";
        email.removeAttribute("aria-invalid");
        if (consent) {
          consent.checked = false;
          consent.removeAttribute("aria-invalid");
        }
        const modal = this.closest("modal-panel");
        if (isModalPanelLike(modal)) modal.close();
      } else {
        this.#showError(formError, this.dataset.errorNetwork ?? "");
      }
    } catch {
      this.#showError(formError, this.dataset.errorNetwork ?? "");
    } finally {
      submit.disabled = false;
      submit.textContent = originalText;
    }
  };

  #collectProperties(): Record<string, string> {
    const properties: Record<string, string> = {};
    const params = new URLSearchParams(window.location.search);
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const value = params.get(key);
      if (value) properties[key] = value;
    }
    properties.signup_url = window.location.origin + window.location.pathname;
    if (document.referrer) properties.signup_referrer = document.referrer;
    return properties;
  }

  #showError(element: HTMLParagraphElement, message: string): void {
    element.textContent = message;
    element.hidden = false;
  }
}

if (!customElements.get("newsletter-signup")) {
  customElements.define("newsletter-signup", NewsletterSignup);
}
